import {Button, Card, Checkbox, Col, ConfigProvider, Divider, Flex, Row, Skeleton, Space, Typography} from "antd";
import {useTranslation} from "react-i18next";
import {capitalize} from "lodash";
import {useCallback} from "react";
import CustomNumericFormat from "../../../common/customNumeric/CustomNumericFormat";
import {useAssetDetails} from "../../../../context/assetDetails/AssetDetails";
import {useHazardSelectionContext} from "../../../../context/assetDetails/HazardAssetSelectionContext";
import {useGetClusterLevelMapQuery} from "../../../../redux/api/hazardTypes";

type DetailsType = {
  vulnerabilityClass?: number,
  impact?: number
}

function Details({vulnerabilityClass, impact}: DetailsType) {

  const {t} = useTranslation()
  const {data} = useGetClusterLevelMapQuery()
  const clusterLevelDescription = data?.find(el => el.value === vulnerabilityClass)?.desc
  const clusterLevelTitle = data?.find(el => el.value === vulnerabilityClass)?.str

  return <>
    <Row>
      <Col offset={12} xs={12}>
        <Typography.Text type={"secondary"}>{capitalize(t("hazards.overallImpact.weightedAverage"))}</Typography.Text>
      </Col>
      <Col xs={12}>
        <Typography.Text type={"secondary"}>{capitalize(t("hazards.overallImpact.totalImpact"))}</Typography.Text>
      </Col>
      <Col xs={12}>
        <CustomNumericFormat
          value={impact}
          displayType={'text'}
          decimalScale={2}
          fixedDecimalScale={true}
        />
      </Col>
      <Col xs={12}>
        <Typography.Text
          type={"secondary"}>{capitalize(t("hazards.overallImpact.totalImpactCluster"))}</Typography.Text>
      </Col>
      <Col xs={12}>
        <Space>
          {capitalize(t("hazards.overallImpact.cluster"))}
          <CustomNumericFormat
            value={vulnerabilityClass}
            displayType={'text'}
          />
        </Space>
      </Col>
      <Col xs={12}>
        <Typography.Text
          type={"secondary"}>{capitalize(t("hazards.overallImpact.clusterLevel"))}</Typography.Text>
      </Col>
      <Col xs={12}>
        {clusterLevelTitle}
      </Col>
      <Col xs={12}>
        <Typography.Text
          type={"secondary"}>{capitalize(t("hazards.overallImpact.clusterType"))}</Typography.Text>
      </Col>
      <Col xs={12}>
        {clusterLevelDescription}
      </Col>
    </Row>
  </>
}

function CardTitle({title}: { title: string }) {
  return <ConfigProvider
    theme={{
      token: {
        colorText: "white",
      }
    }
    }>
    <Typography.Text>{title}</Typography.Text>
  </ConfigProvider>

}

function CardDetails({headerBg, title, details, loading}: {
  headerBg: string,
  title: string,
  details?: DetailsType,
  loading?: boolean
}) {
  return <ConfigProvider
    theme={{
      components: {
        "Card": {
          headerBg,

        }
      },
    }}
  >
    <Card
      loading={loading}
      title={<div style={{textAlign: 'center'}}><CardTitle title={title}/></div>}
    >
      <Details {...details}/>
    </Card>
  </ConfigProvider>
}

export default function OverallImpact() {

  const {t} = useTranslation()
  const [, {asset}] = useAssetDetails();
  const {
    getCalculation,
    additionalOptionsGetCalculation,
    checkedList,
    checkAll,
    indeterminate,
    isNoSelection,
    onChange,
    onCheckAllChange,
    allTheOptions,
    isFetchingHazardTypes,
    hazardTypes
  } = useHazardSelectionContext()
  const {data: overallImpact, isFetching, isUninitialized, originalArgs} = additionalOptionsGetCalculation

  const handleGetData = useCallback(() => {
    getCalculation({
      assetUuid: asset.uuid,
      hazard_type_class_uuids: checkedList as string[]
    })
  }, [asset.uuid, checkedList, getCalculation])

  const loading = isFetching || isUninitialized

  return <Flex vertical gap={"large"}>
    <div>
      <Checkbox
        disabled={isFetchingHazardTypes}
        indeterminate={indeterminate}
        onChange={onCheckAllChange}
        checked={checkAll}>
        {capitalize(t("hazards.overallImpact.selectAllHazards"))}
      </Checkbox>
      <Divider style={{margin: "1rem 0"}}/>
      <Checkbox.Group
        value={checkedList}
        disabled={isFetchingHazardTypes}
        onChange={onChange}
        style={{display: 'flex', gap: '1rem', overflow: 'hidden'}}
      >
        {allTheOptions.map(el => (
          <Checkbox value={el.value}
                    style={{
                      flex: '0 1 200px',
                    }}
                    key={el.key}
          >
            {(el.label)}
          </Checkbox>
        ))}
      </Checkbox.Group>
    </div>
    <div style={{textAlign: 'center'}}>{isNoSelection ?
      <Typography.Paragraph italic>{capitalize(t("actions.selectAtLeastOneOption"))}</Typography.Paragraph> :

      <Button
        type={"primary"}
        onClick={handleGetData}>
        {capitalize(t("actions.calculate"))}
      </Button>

    }
    </div>
    {!isUninitialized && <Row gutter={[16, 16]}>
      <Divider />
      <Col span={24}>
        <Skeleton loading={isFetching}>
          <Typography.Text italic>
            {capitalize(t("hazards.overallImpact.relatedTo"))}: {!originalArgs?.hazard_type_class_uuids ? capitalize(t("hazards.overallImpact.all")) : originalArgs.hazard_type_class_uuids.map(uuid => hazardTypes?.find(ht => ht.uuid === uuid)?.name ?? "").join(", ")}
          </Typography.Text>
        </Skeleton>
      </Col>
      <Divider />
      <Col span={24} style={{display: 'flex', gap: '1rem', flexWrap: 'wrap'}}>
        <div style={{flex: '1 0 400px'}}>
          <CardDetails
            loading={loading}
            headerBg={"#403151"}
            title={capitalize(t("hazards.overallImpact.notMitigated"))}
            details={{
              vulnerabilityClass: overallImpact?.find(el => el.label === 'non_mitigated')?.vulnerability_class,
              impact: overallImpact?.find(el => el.label === 'non_mitigated')?.impact,
            }}

          />
        </div>
        <div style={{flex: '1 0 400px'}}>
          <CardDetails
            loading={loading}
            headerBg={"#215967"}
            title={capitalize(t("hazards.overallImpact.mitigated"))}
            details={{
              vulnerabilityClass: overallImpact?.find(el => el.label === 'mitigated')?.vulnerability_class,
              impact: overallImpact?.find(el => el.label === 'mitigated')?.impact,
            }}
          />
        </div>
      </Col>
    </Row>}
  </Flex>
}